import { apiAgent, apiSolAgent } from "./axiosAgent";
import {downloadFileFromData, showToast} from "../other/utils/utils";
import moment from "moment";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { getIsArchivedForBoard } from "@/other/utils/otherUtils";

// Todo: Remove this endpoint, as it is no longer being used.
export const registerUser = (body) => {
  return apiAgent.post(`user/register?current_time=${encodeURIComponent(moment().format())}`, body);
};

// Todo: Remove this endpoint, as it is no longer being used.
export const loginUser = (body) => {
  return apiAgent.post("user/login", body);
};

export const getDummyBoardData = () => {
  return apiAgent.get("board/");
};

export const loginSocialUser = (body) => {
  return apiAgent.post(`user/social-login?current_time=${encodeURIComponent(moment().format())}`, body);
};

export const loginAsGuest = () => {
  return apiAgent.get(`user/guest-login?current_time=${encodeURIComponent(moment().format())}`);
};

export const getMe = () => {
  return apiAgent.get("user/me");
};

export const userLogout = () => {
  return apiAgent.post("user/logout");
};

// Board Management APIs
export const getUserBoardsList = () => {
  return apiAgent.get("/board/userboard");
}

export const getDemoBoardsList = () => {
  return apiAgent.get('/board/demo-boards')
}

export const getAllRoles = () => {
  return apiAgent.get("/rbac/roles");
}

export const asyncTasksListing = () => {
  return apiAgent.get('/async-task/list');
};

export const asyncTaskCancel = (id) => {
  return apiAgent.post(`async-task/${id}/cancel`);
};

export const markAsyncTaskAsSeen = (id, isSeen = true) => {
  return apiAgent.patch(`async-task/${id}/mark-as-seen`, { is_seen: isSeen });
}

export const asyncTaskResult = (id) => {
  return apiAgent.get(`async-task/${id}/result`);
};

/** Board export (type 7) result is a raw JSON attachment, not `{ meta_data, result }`. */
export const asyncTaskResultFile = (id) => {
  return apiAgent.get(`async-task/${id}/result`, { responseType: "blob" });
};

/** Full async-task record (type, status, meta_data) — preferred over `/result` for hydration. */
export const asyncTaskGet = (id) => {
  return apiAgent.get(`async-task/${id}`);
};

/**
 * @param {string} slug - board slug
 * @param {{display_name: string}} body
 * @param {{string}} boardSessionId
 */
export const updateBoard = (slug, body) => {
  return apiAgent.patch(`/board/${slug}/userboard`, body);
};

/**
 * @param {{display_name: string}} body
 */
export const addBoard = (body) => {
  return apiAgent.post("/board/create", body);
};

/**
 * @param {string} slug - board slug
 */
export const scheduleBoardDelete = (slug) => {
  return apiAgent.delete(`/board/${slug}/schedule-delete`);
}

export const scheduleCancelBoardDelete = (slug) => {
  return apiAgent.post(`/board/${slug}/cancel-delete`);
}

// Data import/export API

export const importDataApi = (file, slug, dataType) => {
  const uploadUrl = `/importexport/board/${slug}/${dataType}`;
  const formData = new FormData();
  formData.append("file", file);
  return apiAgent.post(uploadUrl, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const exportDataApi = (slug, dataType, params = {}) => {
  const url = `/importexport/board/${slug}/${dataType}`;
  return apiAgent.get(url, { params });
};

export const exportBoardApi = (boardId) => {
  const url = `/databridge/${boardId}/export`;
  return apiAgent.get(url);
};

export const importBoardApi = (body) => {
  const url = "/databridge/import";
  return apiAgent.post(url, body);
};

export const getTaskStatusHistory = (id) => {
  return apiAgent.get(`/task/${id}/history`);
};

export const getNotes = (id, controller) => {
  return apiAgent.get(`/notes/board-notes/${id}`, {
    signal: controller.signal
  });
};

export const updateNotes = (id, data) => {
  return apiAgent.put(`/notes/board-notes/${id}`, data);
};

export const duplicateNote = (id, data) => {
  return apiAgent.put(`/notes/board-notes/${id}/duplicate`, data);
};

export const duplicateChecklist = (id, data) => {
  return apiAgent.put(`/checklist/${id}/duplicate`, data);
}

export const getCompanyCamProject = (boardId, id) => {
  return apiAgent.get(`/board/${boardId}/companycam/v2/projects/${id}/`);
};

export const getCompanyCamProjectList = (boardId, queryParams = {}) => {
  return apiAgent.get(`/board/${boardId}/companycam/v2/projects/`, {
    params: { ...queryParams, per_page: 25 },
  });
};

export const validateCompanyCamApiKey = (boardId, apiKey) => {
  return apiAgent.post(`/board/${boardId}/companycam/validate/`, {
    api_key: apiKey,
  });
};

export const getCSVMappings = (slug, importType) => {
  return apiAgent.get(`/importexport/board/${slug}/${importType}/meta`);
}

export const copyEntityToOtherBoard = (entityType, entityId, boardId, options = {}) => {
  const payload = {
    board_id: boardId,
    action: options.action || 'create_new',
    copy_metadata: options.copy_metadata || false,
    [`${entityType}_name`]: options[`${entityType}_name`],
    [`archive_original_${entityType}`]: options[`archive_original_${entityType}`] || false
  };
  return apiAgent.post(`/${entityType}/${entityId}/copy`, payload)
}

export const exportChecklistApi = (checklistId, boardId) => {
  return apiAgent.get(`/board/${boardId}/checklists/${checklistId}/export`, {
    responseType: "blob",
  }).then((res => {
    if (res.data) downloadFileFromData(res.data, "Checklist", "json");
  }))
}

export const exportChecklistItemsApi = (checklistId, fileName = "ChecklistItems") => {
  return apiAgent.get(`/checklist/${checklistId}/items/export`, {
    responseType: "blob",
  }).then((res) => {
    if (res.data) downloadFileFromData(res.data, fileName, "txt");
  })
}

export const getImportTypeUndoPreview = (id) => {
  return apiAgent.get(`/importexport/importbatch/${id}/undo/preview`);
}

export const undoImport = (id) => {
  return apiAgent.post(`/importexport/importbatch/${id}/undo`);
}

export const importChecklistApi = (boardId, data, progressBar, params) => {
  return apiAgent.post(`/board/${boardId}/checklists/import`, data, {
    params,
    onUploadProgress({ loaded, total }) {
      const progress = Math.floor((loaded / total) * 100);
      progressBar.progress = progress;
      progressBar.text = `Importing ${progress}%`
    }
  })
}

export const importChecklistItemsApi = (checklistId, data, progressBar) => {
  return apiAgent.post(`/checklist/${checklistId}/items/import`, { checklist_items_data: data }, {
    onUploadProgress({ loaded, total }) {
      if (progressBar) {
        const progress = Math.floor((loaded / total) * 100);
        progressBar.progress = progress;
        progressBar.text = `Importing ${progress}%`
      }
    }
  })
}

export const uploadImageToChecklistItem = (boardId, checklistItemId, image) => {
  const form = new FormData();
  form.append("file", image)
  return apiAgent.post(`/board/${boardId}/checklists/item/${checklistItemId}/upload`, form);
}

export const sendNudgeAlert = (checklistId) => {
  return apiAgent.post(`/checklist/${checklistId}/nudge`)
}

export const requestSandboxBoard = (rep, onSuccess) => {
  rep
    .then((res) => {
      if (res?.data?.id) {
        onSuccess(res.data);
      } else throw new Error();
    })
    .catch((err) => {
      showToast({
        msg:
          err?.response?.data?.detail ||
          "We're having trouble connecting.  Please try again.",
        type: "error"
      });
      onSuccess(null);
    });
};

export const uploadFile = (file, fileName, otherConfig = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("filename", fileName);
  return apiAgent.post("/document/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    ...otherConfig
  })
}

/** Board MCP workflow markdown guide (PrivateDocument linked on the board). */
export const getMcpWorkflowDocument = (boardId) =>
  apiAgent.get(`/board/${boardId}/mcp-workflow-document/`);

/** Board owner: set or clear (`document_id: null`) the workflow document id after upload. */
export const setMcpWorkflowDocument = (boardId, documentId) =>
  apiAgent.put(`/board/${boardId}/mcp-workflow-document/`, { document_id: documentId });

/** Short-lived download URL for the board MCP workflow guide; do not cache past expires_in. */
export const getMcpWorkflowDocumentDownloadUrl = (boardId) =>
  apiAgent.get(`/board/${boardId}/mcp-workflow-document/presigned-url`);

export const leaveBoard = (slug) => {
  return apiAgent.post(`/board/${slug}/leave-board`);
};

export const checkIfBudgetIdExists = (boardId, budgetId) => {
  return apiAgent.post(`/task/budget/exists`, { board_id: boardId, budget_id: budgetId });
};

export const checklistItemImageDelete = (id) => {
  return apiAgent.patch(`/checklist/item/${id}`, {
    remove_file: true
  });
};

export const getSwivuAssitText = (boardId) => {
  return apiAgent.get(`/swivuassist/list?board_id=${boardId}`);
}

export const updateSwivuAssistText = (boardId = null) => {
  return apiAgent.post(`/swivuassist/update-from-sheet`, null, { params: { board_id: boardId }})
}

export const updateWFBoardActiveState = (boardId, data) => {
  return apiAgent.patch(`userpreference/board/${boardId}/board-workforce-state`, data);
}

export const duplicateBoardAndTestDrive = (boardId, name, boardDataCopyOptions, params = null) => {
  return apiAgent.post(`board/duplicate`, { source_board_id: boardId, display_name: name, selected_options: boardDataCopyOptions }, {
     params,
  });
}

export const exportBoardView = (viewId, showArchived = false, fileName) => {
  return apiAgent.get(`importexport/boardview/${viewId}/export`, {
    params: { include_archived: showArchived },
    responseType: 'blob'
  }).then((response) => {
    if (response.data) {
      downloadFileFromData(response.data, fileName, 'csv');
    }
  }).catch(() => {
    showToast({
      msg: "Failed to export view",
      type: "error"
    });
  });
}

export const generateAIInsight = (body, signal) => {
  return apiAgent.post("inspection/test-insight", body, { signal });
};

export const assignAITags = (body) => {
  return apiAgent.post("inspection/assign-tags", body);
};

export const publishContextCards = (boardId, inspectionType) => {
  return apiAgent.post("inspection/context-cards/publish", { board_id: boardId, inspection_type: inspectionType });
};

export const revertToFactoryDefaults = (boardId, inspectionType) => {
  return apiAgent.post("inspection/context-cards/reset-to-default", { board_id: boardId, inspection_type: inspectionType });
};

export const getDraftContextPrompt = (boardId, inspectionType) => {
  return apiAgent.get(`inspection/board/${boardId}/draft-context-prompt?inspection_type=${inspectionType}`);
};

// Field Advisor Cards APIs
export const publishFieldAdvisorCards = (boardId, advisorType) => {
  return apiAgent.post("inspection/field-advisor-cards/publish", {
    board_id: boardId,
    advisor_type: advisorType
  });
};

export const revertFieldAdvisorToDefault = (boardId, advisorType) => {
  return apiAgent.post("inspection/field-advisor-cards/reset-to-default", {
    board_id: boardId,
    advisor_type: advisorType
  });
};

export const getDraftFieldAdvisorPrompt = (boardId, advisorType) => {
  return apiAgent.get(`inspection/board/${boardId}/draft-fieldadvisor-prompt?advisor_type=${advisorType}`);
};

export const getPresignedImageUrlForFile = (fileId) => {
  return apiAgent.get(`file/${fileId}/presigned-url`);
};

export const getPresignedImageUrlForUnassignedFile = (fileId) => {
  return apiAgent.get(`file/unassigned/${fileId}/presigned-url`);
};

export const getBoardUserOwnership = (id) => {
  const selectedBoard = getSavedBoardSelection();
  const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);
  return apiAgent.get(`/board/board-user/${id}/ownership`, {
    params: { include_archived: canShowArchivedRecords }
  });
};

// Insights APIs
export const generateInsight = (body, signal) => {
  return apiAgent.post("inspection/generate-insight", body, { signal });
};

export const cancelInsightGeneration = (body) => {
  return apiAgent.post("inspection/cancel-insight-generation", body);
};

export const getInsightTypeAvatar = (insightType) => {
  return apiAgent.get(`inspection/${insightType}/avatar`);
};

export const createUnassignedFile = (documentId) => {
  return apiAgent.post("/file/unassigned/create", { document_id: documentId });
};

export const getUnassignedFiles = () => {
  return apiAgent.get("/file/unassigned/list");
};

export const deleteUnassignedFile = (id) => {
  return apiAgent.delete(`/file/unassigned/${id}/delete`);
};

export const setInsightDefaultBoard = (boardId) => {
  return apiAgent.put("inspection/preferences/insight-generation-default-board", { board_id: boardId });
};

export const getInsightDefaultBoard = () => {
  return apiAgent.get("inspection/preferences/insight-generation-default-board");
};

export const getUnassignedFileInsights = (unassignedFileId, params = {}) => {
  return apiAgent.get(`file/unassigned/${unassignedFileId}/insights`, { params });
};

export const deleteInspectionInsight = (insightId) => {
  return apiAgent.delete(`inspection/insights/${insightId}`);
};

// Integration health APIs
export const getIntegrationFailures = (boardId, params = {}) => {
  return apiAgent.get(`/board/${boardId}/integrations/failures`, { params });
};

// Swivu Sol V2 Chat API (non-streaming ask)
export const sendChatMessage = (body) => {
  return apiSolAgent.post("/api/solv2/chat/ask", body);
};

export const getChatSessions = (params = {}) => {
  return apiSolAgent.get("/api/chat/sessions", { params });
};

export const getChatSessionHistory = (sessionId) => {
  return apiSolAgent.get(`/api/chat/sessions/${sessionId}/history`);
};

export const deleteChatSession = (sessionId) => {
  return apiSolAgent.delete(`/api/chat/sessions/${sessionId}`);
};


// Document AI Extraction APIs
export const extractDates = (body, signal) => {
  return apiAgent.post("document-ai/extract-dates/", body, { signal });
};

export const extractSummary = (body, signal) => {
  return apiAgent.post("document-ai/extract-summary/", body, { signal });
};

export const extractChecklist = (body, signal) => {
  return apiAgent.post("document-ai/extract-checklist/", body, { signal });
};

export const extractLocation = (body, signal) => {
  return apiAgent.post("document-ai/extract-location/", body, { signal });
};

export const extractBudget = (body, signal) => {
  return apiAgent.post("document-ai/extract-budget/", body, { signal });
};

export const extractClient = (body, signal) => {
  return apiAgent.post("document-ai/extract-client/", body, { signal });
};

export const extractNotes = (body, signal) => {
  return apiAgent.post("document-ai/extract-notes/", body, { signal });
};

export const extractDocumentText = (body, signal) => {
  return apiAgent.post("document-ai/extract-document-text/", body, { signal });
};

export const bulkCopyEntitiesToBoard = (entityType, entityIds, boardId, options = {}) => {
  return apiAgent.post(`/${entityType}/bulk-copy`, {
    board_id: boardId,
    [`${entityType}_ids`]: entityIds,
    action: options.action || 'duplicate',
    copy_metadata: options.copy_metadata || false,
    [`archive_original_${entityType}`]: options.archive_original || false,
  });
};

// Settings Configuration APIs
export const uploadSettingsConfig = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return apiAgent.post("/document/settings-config/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const getSettingsConfig = () => {
  return apiAgent.get("/document/settings-config");
};

// Marketing / Promote APIs
export const publishPromoteCards = (boardId) => {
  return apiAgent.post("promote/prompt-cards/publish", { board_id: boardId });
};

export const revertPromoteToDefault = (boardId) => {
  return apiAgent.post("promote/prompt-cards/reset-to-default", { board_id: boardId });
};

export const getDraftPromotePrompt = (boardId) => {
  return apiAgent.get(`promote/board/${boardId}/draft-promote-prompt`);
};

export const testPromoteSuggest = (documentId, boardId) => {
  return apiAgent.post("promote/test-suggest", { document_id: documentId, board_id: boardId });
};

export const promoteFile = (fileId) => {
  return apiAgent.post("promote/suggest", { file_id: fileId });
};

export const importMarketingTagsApi = (file, slug) => {
  const formData = new FormData();
  formData.append("file", file);
  return apiAgent.post(`promote/board/${slug}/import-tags`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const exportMarketingTagsApi = (slug) => {
  return apiAgent.get(`promote/board/${slug}/export-tags`, {
    responseType: "blob",
  });
};

// Marketing Work File APIs
export const marketingWorkFileApi = (slug) => ({
  generate: (payload) =>
    apiAgent.post(`promote/board/${slug}/marketing-work-files/generate`, payload),
});

/** Async 202 Accepted — body is AsyncTask record; poll `/async-task/{id}/result` as usual. */
export const generateMarketingPage = (slug, workFileId) =>
  apiAgent.post(`promote/board/${slug}/page/generate`, { work_file_id: workFileId });

/**
 * Async 202 — poll `/async-task/{id}/result`. Path uses numeric board_id (not slug).
 */
export const generateMarketingDocument = (boardId, { workFileId, format, name }) =>
  apiAgent.post(`promote/board/${boardId}/document/generate`, {
    work_file_id: workFileId,
    format,
    name,
  });

/** Short-lived JWT download URL; open `download_url` (do not cache). */
export const getMarketingDocumentDownloadLink = (boardId, documentId) =>
  apiAgent.get(`promote/board/${boardId}/document/${documentId}/download-link`);

export const marketingPagesApi = (slug) => {
  const pageBase = (pageId) => `promote/board/${slug}/page/${pageId}`;
  return {
    uploadOgImage: (pageId, file) => {
      const form = new FormData();
      form.append("file", file);
      return apiAgent.post(`${pageBase(pageId)}/og-image`, form);
    },
    removeOgImage: (pageId) => apiAgent.delete(`${pageBase(pageId)}/og-image`),
  };
};

// ─── Site APIs ────────────────────────────────────────────────────────────────

export const sitesApi = (slug) => {
  const base = `sites/board/${slug}`;
  return {
    publish: () => apiAgent.post(`${base}/publish`),
    publishWithDomain: () => apiAgent.post(`${base}/publish-with-domain`),
    generateSeo: () => apiAgent.post(`${base}/generate-seo`),
    uploadOgImage: (file) => {
      const form = new FormData();
      form.append("file", file);
      return apiAgent.post(`${base}/og-image`, form);
    },
    removeOgImage: () => apiAgent.delete(`${base}/og-image`),
    uploadFavicon: (file) => {
      const form = new FormData();
      form.append("file", file);
      return apiAgent.post(`${base}/favicon`, form);
    },
    removeFavicon: () => apiAgent.delete(`${base}/favicon`),
    saveDomain: (body) => apiAgent.post(`${base}/domain`, body),
    getDomainStatus: (params) => apiAgent.get(`${base}/domain/status`, { params }),
    confirmDomain: () => apiAgent.post(`${base}/domain/confirm`),
    removeDomain: () => apiAgent.delete(`${base}/domain/remove`),
  };
};

/** HQ Leads — list/detail/convert submission → task */
export const submissionsApi = (slug) => {
  const base = `sites/board/${slug}/submissions`;
  return {
    list: () => apiAgent.get(base),
    detail: (id) => apiAgent.get(`${base}/${id}`),
    convertPreview: (id) => apiAgent.get(`${base}/${id}/convert-preview`),
    convertToTask: (id) => apiAgent.post(`${base}/${id}/convert-to-task`),
  };
};

/** Board-id variant for domain routes when slug is unavailable. */
export const sitesApiByBoardId = (boardId) => {
  const base = `sites/board/${boardId}`;
  return {
    saveDomain: (body) => apiAgent.post(`${base}/domain`, body),
    getDomainStatus: (params) => apiAgent.get(`${base}/domain/status`, { params }),
    confirmDomain: () => apiAgent.post(`${base}/domain/confirm`),
    removeDomain: () => apiAgent.delete(`${base}/domain/remove`),
  };
};

// Marketing Manifest APIs
export const marketingManifestApi = (slug) => {
  const base = `promote/board/${slug}/marketing-manifest`;
  return {
    get: () => apiAgent.get(base),
  };
};

// ─── Board Context APIs ───────────────────────────────────────────────────────

export const boardContextApi = (slug) => {
  const base = `board-context/board/${slug}/board-context`;
  return {
    // Board-scoped (no contextId needed)
    mapUrl: (url) => apiAgent.post(`${base}/map-url`, { url }),
    // Context-scoped wizard calls (contextId required per backend API)
    generateQuestions: (contextId) => apiAgent.post(`${base}/${contextId}/generate-questions`, {}),
    generateContext: (contextId, questions) => apiAgent.post(`${base}/${contextId}/generate-context`, { questions }),
    suggest: (contextId) => apiAgent.post(`${base}/${contextId}/suggest`, {}),
    // Markdown export — returns a blob (text/markdown file download)
    exportMarkdown: (contextId) =>
      apiAgent.get(`${base}/${contextId}/export`, { responseType: "blob" }),
  };
};

// ─── Location AI generation ───────────────────────────────────────────────────

/**
 * POST location/generate-summary
 * @param {object} payload request body, must include `board`
 * @returns {Promise<{data:{location_summary:string,workforce_handoff_note:string}}>}
 */
export const generateLocationSummary = (payload) => {
  return apiAgent.post("location/generate-summary", payload);
};

/**
 * Backwards-compatible helper (legacy per-field API).
 * Prefer `generateLocationSummary(payload)` going forward.
 *
 * @param {object} payload request body, must include `board`
 * @param {"locationSummary"|"workforceHandoffNote"} fieldType
 * @returns {Promise<{data:{result:string}}>}
 */
export const generateLocationFieldSummary = async (payload, fieldType) => {
  const resp = await generateLocationSummary(payload);
  const result =
    fieldType === "locationSummary"
      ? resp?.data?.location_summary
      : resp?.data?.workforce_handoff_note;
  return { data: { result: result ?? "" } };
};

// ─── Clocking APIs ────────────────────────────────────────────────────────────

/** Board-scoped Clocking REST (V1 — not a Bryntum sync store). */
export const clockingApi = (boardId) => {
  const base = `board/${boardId}/clocking`;
  return {
    getAccess: () => apiAgent.get(`${base}/access`),
    getAttendance: (params) => apiAgent.get(`${base}/attendance`, { params }),
    getAttendanceDetail: (attendanceId) => apiAgent.get(`${base}/attendance/${attendanceId}`),
    markReviewed: (attendanceId, body = {}) =>
      apiAgent.post(`${base}/attendance/${attendanceId}/mark-reviewed`, body),
    excuse: (attendanceId, body = {}) =>
      apiAgent.post(`${base}/attendance/${attendanceId}/excuse`, body),
    addNote: (attendanceId, body) =>
      apiAgent.post(`${base}/attendance/${attendanceId}/note`, body),
    correct: (attendanceId, body) =>
      apiAgent.post(`${base}/attendance/${attendanceId}/correct`, body),
    getTimeOff: (timeOffId) => apiAgent.get(`${base}/time-off/${timeOffId}`),
    patchTimeOff: (timeOffId, body) => apiAgent.patch(`${base}/time-off/${timeOffId}`, body),
    getSettings: () => apiAgent.get(`${base}/settings`),
    patchSettings: (body) => apiAgent.patch(`${base}/settings`, body),
  };
};
